<template>
<div id="app" class="home page-template-default page page-id-13 wp-custom-logo wp-embed-responsive post-image-aligned-center sticky-menu-fade sticky-enabled both-sticky-menu no-sidebar nav-below-header separate-containers header-aligned-left dropdown-hover">
  <HeadNav selectedLanguage="en" page="instasave"  
      :gotodownload="gotodownload"
      :apk="apk"/>
  <div id="page" class="site container hfeed">
    <div id="content" class="site-content">
      <div id="primary" class="content-area">
        <main id="maincontent" class="site-main">
          <!-- Hero Section -->
          <HeroSection 
            :title="heroTitle"
            :subtitle="heroSubtitle"
            :input-placeholder="inputPlaceholder"
            :active-tab="activeTab"
            @tab-change="switchTab"
            @download-content="downloadContent"
            @go-to-download="goToDownload"
            @join-telegram="joinTelegramChannel"
          />
          <div class="home-wrapper">
           

            <!-- How to Download Section -->
            <HowToSection
              :title="'How to Download Video & Photos from Instagram - InstaSave'"
              :steps="howToSteps"
            />

            <!-- Why Use InstaSave Section -->
            <section class="why-use-section">
              <h2 class="section-title">Why Use Instasave for Instagram (IG) Download?</h2>
              <ul class="benefits-list">
                <li>
                  <h3>Unlimited Downloads</h3>
                  <p>No installation, no login required. Download as many videos and photos as you want every day without any restrictions.</p>
                </li>
                <li>
                  <h3>100% Anonymous</h3>
                  <p>Works on mobile and desktop. Stay completely anonymous when browsing and downloading from Instagram without sharing any data.</p>
                </li>
                <li>
                  <h3>Fast & Completely Free</h3>
                  <p>Handles posts, reels, carousel photos, and some public story links. Our service is fully automated, fast, safe and completely free.</p>
                </li>
              </ul>
            </section>

            <!-- App Recommendation Section -->
            <AppRecommendationSection 
              :title="appRecommendationTitle"
              :description="appRecommendationDescription"
              :button-text="appRecommendationButtonText"
              :image-src="appRecommendationImageSrc"
              :image-alt="appRecommendationImageAlt"
              @go-to-download="goToDownload"
            />

            <!-- Why Choose Online First Section -->
            <section class="choice-section">
              <div class="choice-card">
                <h3>Why Choose Online First, InstaSave App Second?</h3>
                <ul>
                  <li><strong>Online:</strong> fastest for one-off saves, no install, works on any device.</li>
                  <li><strong>App:</strong> best for power users—bulk downloads, background mode, offline gallery, and smarter file naming.</li>
                  <li>If you want to share your experience, learn new methods, or solve problems with Instagram content download, feel free to join the GB WhatsApp group for discussion.</li>
                </ul>
          </div>
            </section>

            <!-- Features Sections -->
            <FeaturesGrid :features="features" />

            <!-- FAQ Section -->
            <section class="faq-section">
              <h2 class="section-title">FAQ & Troubleshooting</h2>
              <div class="faq-container">
                <div v-for="(faq, index) in faqs" :key="index" class="faq-item">
                  <div class="faq-question" @click="toggleFaq(index)">
                    <div style="font-size: 1.2rem; font-weight: 600;">{{ faq.question }}</div>
                    <span class="faq-toggle">{{ expandedFaqs.includes(index) ? '−' : '+' }}</span>
                  </div>
                  <div v-if="expandedFaqs.includes(index)" class="faq-answer">
                    <p v-html="faq.answer"></p>
                  </div>
                </div>
              </div>
            </section>
            <p>
              For a safer and more private browsing experience, you can also use a reliable
              <a href="https://secureproxyvpn.com" target="_blank" rel="noopener noreferrer">Secure VPN</a>
              service such as Secure VPN to protect your connection while accessing online content.
            </p>
          </div>
        </main> 
      </div>
    </div>
  </div>

</div>
</template>

<script>
import '@/css/base.scss';
import '@/css/index.scss';
import HeadNav from '@/components/HeadNav.vue';
import Footer from '@/components/Footer.vue';
import HeroSection from '@/components/HeroSection.vue';
import HowToSection from '@/components/HowToSection.vue';
import FeaturesGrid from '@/components/FeaturesGrid.vue';
import AppRecommendationSection from '@/components/AppRecommendationSection.vue';

export default {
  name: 'Home',
  components: {
    HeadNav,
    Footer,
    HeroSection,
    HowToSection,
    FeaturesGrid,
    AppRecommendationSection
  },
  head() {
    return {
      title: 'InstaSave – Instagram Videos & Photos Download',
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: 'InstaSave helps you to download Instagram videos and photos online easily. Paste the link, click download, and save your favorite IG content in high quality.'
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: 'Instagram video download, instasave, instagram photo downloader'
        }
      ]
    }
  },
  data() {
    return {
      pageName: 'home',
      instagramLink: '',
      expandedFaqs: [],
      activeTab: '',
      heroTitle: 'InstaSave – Instagram Downloder',
      heroSubtitle: 'Simply paste the Instagram link and click the download button',
      inputPlaceholder: 'Paste link here',
      appRecommendationTitle: 'InstaSave: Download Instagram on Android (App Recommendation)',
      appRecommendationDescription: 'If you save content frequently, an app is more convenient. InstaSave is built for Android and focuses on speed, batch downloads, and an organized gallery.',
      appRecommendationButtonText: 'Free Download InstaSave',
      appRecommendationImageSrc: require('@/assets/home/home-1.webp'),
      appRecommendationImageAlt: 'InstaSave App',
      faqs: [
        {
          question: "Do I need to log in to download?",
          answer: "No for most public posts via the online tool. Some Stories or private content require access you already have in the Instagram app; our Android app can help when you share a link you can view."
        },
        {
          question: "The download has no sound—why?",
          answer: "Some tracks use licensed audio that third-party tools can't bundle. Try downloading through the app's \"original audio\" option if available, or save a different version from the creator (with permission)."
        },
        {
          question: "Why is the video blurry after saving?",
          answer: "Instagram compresses media. Choose the highest quality offered at download time. On Android, enable \"Prefer HD\" in Instasave settings."
        },
        {
          question: "Can I download from private accounts?",
          answer: "Only if you follow the account and can view the content in your app—then share/copy the link to Instasave. Never bypass privacy settings."
        },
        {
          question: "Where do my files go?",
          answer: "Desktop: your browser's Downloads.<br>Android: Internal storage › Pictures/Movies › Instasave (configurable in settings).<br>iPhone (online tool): Files app or Photos (depending on your save choice)."
        },
        {
          question: "The link says \"invalid\"",
          answer: "Ensure you copied the full URL (no extra spaces). Reopen Instagram, tap Share › Copy Link, then paste again."
        },
        {
          question: "Will my account get banned for using an app?",
          answer: "We never ask for your Instagram password. Still, always follow Instagram's rules and only download permissible content."
        },
        {
          question: "Can I schedule or batch downloads?",
          answer: "Yes—on Android, paste multiple links into Instasave, or enable clipboard auto-capture to queue files."
        },
        {
          question: "Does it work for Carousels and Highlights?",
          answer: "Yes. Select individual items (online) or choose Download all (app) for convenience."
        },
        {
          question: "Is this free?",
          answer: "The online downloader and Instasaver for Android are free."
        }
      ],
      howToSteps: [
        {
          title: "Step 1: Copy the Instagram Link",
          items: [
            "Open Instagram (app or web).",
            "Tap the three-dot icon (or Share) on a post, Reel, Story highlight, or IGTV.",
            "Choose Copy link."
          ]
        },
        {
          title: "Step 2: Paste the Link",
          items: [
            "Return here and paste the link into the download box."
          ]
        },
        {
          title: "Step 3: Download Video/Photos",
          items: [
            "Hit Download.",
            "Choose Video or Photo (if a carousel, pick files individually).",
            "Save to your device (mobile: Files/Photos, desktop: Downloads folder)."
          ]
        }
      ],
      features: [
        {
          title: "Download Instagram Video",
          description: "With our online tool, you can easily download Instagram videos in just one click. Copy the video link, paste it into the box, and save it directly to your device in MP4 format. No software needed, and works on both desktop and mobile.",
          descriptionHtml: "With our online tool, you can easily <a href=\"https://snapinsta.club/\" target=\"_blank\" rel=\"noopener noreferrer\">download Instagram videos</a> in just one click. Copy the video link, paste it into the box, and save it directly to your device in MP4 format. No software needed, and works on both desktop and mobile.",
          imageSrc: require('@/assets/home/home-2.webp'),
          imageAlt: "Download Instagram Video"
        },
        {
          title: "Download Instagram Photos",
          description: "Use our downloader to quickly download Instagram photos in high resolution. Whether it's a single picture or a full carousel post, just paste the link and save the images instantly. Fast, simple, and completely online.",
          imageSrc: require('@/assets/home/home-3.webp'),
          imageAlt: "Download Instagram Photos"
        },
        {
          title: "Instagram Story Saver",
          description: "Our Instagram Story Saver lets you save story from Instagram before they disappear. Copy the Story link, paste it into the tool, and download it directly to your phone or computer. Works for highlights and public stories with no login required.",
          imageSrc: require('@/assets/home/home-4.webp'),
          imageAlt: "Instagram Story Saver"
        },
        {
          title: "Instagram Reels Download",
          description: "Want to keep Reels offline? Our Instagram Reels download feature allows you to save any Reel as an MP4 file. Just paste the Reel link into the tool and download it in high quality to watch anytime, anywhere.",
          imageSrc: require('@/assets/home/home-5.webp'),
          imageAlt: "Instagram Reels Download"
        },
        {
          title: "Instagram Post Download",
          description: "With our Instagram post download option, you can save complete posts including photos, videos, or carousel albums. Simply copy the post link and paste it to download all media from the post instantly.",
          imageSrc: require('@/assets/home/home-6.webp'),
          imageAlt: "Instagram Post Download"
        },
        {
          title: "IGTV Download",
          description: "Our IGTV download feature makes it simple to save long-form Instagram videos. Copy the IGTV link, paste it into the downloader, and get the video in MP4 format for offline viewing. Perfect for tutorials, shows, and interviews.",
          imageSrc: require('@/assets/home/home-7.webp'),
          imageAlt: "IGTV Download"
        }
      ]
    };
  },
  computed: {
    currentTabTitle() {
      const currentTab = this.navTabs.find(tab => tab.id === this.activeTab);
      return currentTab ? currentTab.title : 'Instagram Download';
    }
  },
  methods: {
    switchTab(tabId) {
      this.activeTab = tabId;
      console.log('Switching to tab:', tabId);
    },
    pasteFromClipboard() {
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText().then(text => {
          this.instagramLink = text;
        }).catch(err => {
          console.error('Failed to read clipboard contents: ', err);
        });
          } else {
        // Fallback for older browsers
        alert('Please paste the link manually');
      }
    },
    downloadContent() {
      // 直接下载APK文件，参考instagram-apk-download页面的实现
      let apkUrl = 'https://apk.instasaves.io/app/android/apk/Instagram_367.0.0.44.74.apk';
      this.$global.download(apkUrl);
      console.log('Downloading APK:', apkUrl);
    },
    toggleFaq(index) {
      const expandedIndex = this.expandedFaqs.indexOf(index);
      if (expandedIndex > -1) {
        this.expandedFaqs.splice(expandedIndex, 1);
          } else {
        this.expandedFaqs.push(index);
          }
    },
    gotodownload() {
      // 直接下载APK文件
      let apkUrl = 'https://apk.instasaves.io/app/android/apk/Instagram_367.0.0.44.74.apk';
      this.$global.download(apkUrl);
      console.log('Download APK:', apkUrl);
    },
    goToDownload() {
      // 跳转到下载页面
      this.$router.push('/instagram-apk-downlad/');
    },
    joinTelegramChannel() {
      // 跳转到Telegram频道
      window.open('https://t.me/+M75kow80SlQ1MjZl', '_blank');
    },
  },
};
</script>

<style scoped>

.home-wrapper {
  max-width: 1000px;
  margin: 0 auto;
  margin-top: 1rem;
}

/* Hero Section - 重新设计 */
.hero-section {
  background: linear-gradient(80deg, #D009FF 0%, #3b82f6 100%);
  color: white;
  padding: 60px 20px 80px;
  text-align: center;
  margin-bottom: 40px;
  position: relative;
  overflow: hidden;
}

.hero-content {
  max-width: 1000px;
  margin: 0 auto;
}

/* Logo - 移除，因为图片中没有显示logo */
.hero-logo {
  display: none;
}

/* Navigation Tabs */
.nav-tabs {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 50px;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 8px;
  border-radius: 30px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 50px;
}

.nav-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 1);
  flex: 1;
  justify-content: center;
}

.nav-tab:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-tab.active {
  background: rgba(255, 255, 255, 0.25);
  color: white;
  font-weight: 600;
}

.tab-icon {
  font-size: 14px;
}

.tab-text {
  font-weight: inherit;
}

/* Main Title */
.hero-title {
  font-size: 2.8rem;
  font-weight: 600;
  color: white;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
  letter-spacing: -0.5px;
}

/* Input Section */
.paste-link-container {
  max-width: 600px;
  margin: 0 auto;
}

.coming-soon-note {
  text-align: center;
  margin-top: 15px;
  font-size: 14px;
  color: rgba(255, 255, 255, 1);
  font-style: italic;
  opacity: 0.9;
}

.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  flex-wrap: wrap;
}

.download-app-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.download-app-btn:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.telegram-btn {
  background: transparent;
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.3);
  padding: 0px 22px;
  height: min-content;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.telegram-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-2px);
}

.btn-icon {
  width: 34px;
  height: 34px;
  object-fit: contain;
}

.input-group {
  display: flex;
  gap: 0;
  background: white;
  padding: 4px;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  align-items: center;
  border: 1px solid rgba(0,0,0,0.05);
}

.link-input {
  flex: 1;
  border: none;
  padding: 16px 20px;
  font-size: 15px;
  border-radius: 8px;
  outline: none;
  background: transparent;
  color: #333;
}

.link-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.paste-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 16px;
  background: #e5e7eb;
  color: #6b7280;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;
  margin-right: 4px;
}

.paste-btn:hover {
  background: #d1d5db;
  color: #374151;
}

.paste-icon {
  font-size: 14px;
}

.download-btn {
  background: #ef4444;
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.download-btn:hover {
  background: #dc2626;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4);
}

/* Section Styles - 简洁的标题设计 */
.section-title {
  text-align: center;
  margin-bottom: 40px;
  color: #333;
}


/* Why Use Section - 浅灰背景 */
.why-use-section {
  padding: 40px 20px;
  background: #f8f9fa;
}

.benefits-list {
  max-width: 1000px;
  margin: 0 auto;
  list-style: none;
  padding: 0;
  /* display: grid; */
  /* grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)); */
  gap: 40px;
}

.benefits-list li {
  margin-bottom: 20px;
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(0,0,0,0.08);
  font-size: 16px;
  color: #555;
  text-align: left;
  position: relative;
  padding-left: 80px;
  transition: all 0.3s ease;
  border: 1px solid rgba(102, 126, 234, 0.1);
  list-style: none;
}

.benefits-list li:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0,0,0,0.12);
  border-color: rgba(102, 126, 234, 0.2);
}

.benefits-list li::before {
  content: '';
  position: absolute;
  top: 30px;
  left: 15px;
  width: 50px;
  height: 50px;
  border-radius: 15px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: white;
  font-weight: bold;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.benefits-list li:nth-child(1)::before {
  content: '∞';
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.benefits-list li:nth-child(2)::before {
  content: '👤';
  background: linear-gradient(135deg, #f093fb, #f5576c);
}

.benefits-list li:nth-child(3)::before {
  content: '⚡';
  background: linear-gradient(135deg, #4facfe, #00f2fe);
}

.benefits-list li h3 {
  margin: 0 0 15px 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #2c3e50;
}

.benefits-list li p {
  margin: 0;
  line-height: 1.6;
  color: #666;
}

/* App Recommendation Section */

/* Choice Section - 白色背景 */
.choice-section {
  padding: 30px 20px;
  background: white;
}

.choice-card {
  max-width: 800px;
  margin: 0 auto 30px;
  background: #f8f9fa;
  padding: 30px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.choice-card h3 {
  font-size: 1.4rem;
  margin-bottom: 20px;
  color: #333;
  font-weight: 600;
}

.choice-card ul {
  list-style: none;
  padding: 0;
}

.choice-card li {
  padding: 12px 0;
  color: #666;
  font-size: 18px;
  border-bottom: 1px solid #e9ecef;
}

.choice-card li:last-child {
  border-bottom: none;
}

.choice-image {
  /* text-align: center; */
  width: 230px;
  height: 230px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.choice-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}


/* FAQ Section - 现代化折叠设计 */
.faq-section {
  padding: 20px 20px;
  background: white;
  margin-bottom: 20px;
}

.faq-container {
  max-width: 800px;
  margin: 0 auto;
}

.faq-item {
  background: white;
  margin-bottom: 20px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.faq-item:hover {
  box-shadow: 0 8px 25px rgba(0,0,0,0.15);
}

.faq-question {
  padding: 25px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.3s ease;
}

.faq-question:hover {
  background: #f8f9fa;
}

.faq-question h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #2c3e50;
  font-weight: 600;
}

.faq-toggle {
  font-size: 1.5rem;
  font-weight: bold;
  color: #667eea;
  transition: transform 0.3s ease;
}

.faq-item.expanded .faq-toggle {
  transform: rotate(45deg);
}

.faq-answer {
  padding: 0 25px 25px;
  color: #555;
  line-height: 1.6;
    font-size: 16px;
  }
  
/* Responsive Design */
@media (max-width: 768px) {
  .hero-section {
    padding: 40px 20px 60px;
  }
  
  .logo-sss {
    font-size: 2rem;
  }
  
  .logo-instagram {
    font-size: 1.5rem;
  }
  
  .nav-tabs {
    gap: 6px;
    margin-bottom: 40px;
    padding: 6px;
    border-radius: 25px;
  }
  
  .nav-tab {
    padding: 8px 12px;
    font-size: 11px;
  }
  
  .hero-title {
    font-size: 2rem;
    margin-bottom: 40px;
  }
  
  .input-group {
    flex-direction: column;
    gap: 8px;
    border-radius: 12px;
    padding: 12px;
  }
  
  .link-input, .paste-btn, .download-btn {
    border-radius: 8px;
    width: 100%;
  }
  
  .paste-btn {
    margin-right: 0;
    margin-bottom: 8px;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  
  .app-content {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .app-image {
    width: 200px;
    height: 200px;
  }
  
  
  .feature-image {
    width: 200px;
    height: 200px;
  }
  
  .choice-image {
    width: 200px;
    height: 200px;
  }
  
  .choice-card {
    padding: 25px;
  }
  
  .benefits-list {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .benefits-list li {
    padding: 30px 25px;
    padding-left: 70px;
  }
  
  .benefits-list li::before {
    width: 45px;
    height: 45px;
    top: 25px;
    left: 20px;
    font-size: 20px;
  }
  
  .faq-question {
    padding: 20px;
  }
  
  .faq-answer {
    padding: 0 20px 20px;
}
}
</style>
